import { GridLogicOperator } from "@mui/x-data-grid-pro";
import { useCustomState } from "../../utils/useCustomState";

/**
 * Hook para manejar el estado común de todos los boards
 * @param {Object} config - Configuración específica del board
 * @param {string} config.status - Estado inicial del board
 * @param {Array} config.groups - Grupos del board
 * @param {Array} config.pinnedColumnsLeft - Columnas fijadas a la izquierda
 * @param {Array} config.pinnedColumnsRight - Columnas fijadas a la derecha
 * @param {Object} config.additionalState - Estado adicional específico del board
 * @returns {Array} [state, updateState] - Estado y función para actualizarlo
 */
export const useBoardState = (config = {}) => {
  const {
    status = "Open",
    groups = ["Open", "Done"],
    pinnedColumnsLeft = ["__check__", "threads"],
    pinnedColumnsRight = [],
    additionalState = {},
  } = config;

  // Estado base común a todos los boards
  const baseState = {
    // Estados de filtrado y vista
    status,
    groups,
    viewFilter: {
      items: [],
      quickFilterLogicOperator: GridLogicOperator.Or,
    },
    columnsFilter: { id: false },
    pinnedColumns: {
      left: pinnedColumnsLeft,
      right: pinnedColumnsRight,
    },
    
    // Estados de selección y actualización
    selectionRow: [],
    updateRows: [],
    refreshState: false,
    
    // Estados de vista
    selectedView: "tabla-principal",
    position: [0],
    
    // Estados de UI
    windowWidth: window.innerWidth,
    posting: false,
    
    // Arrays de datos - estos varían por board pero son comunes
    items: [],
    users: [],
    
    // Combinar con estado adicional específico del board
    ...additionalState,
  };

  return useCustomState(baseState);
};

// Configuraciones predefinidas para boards comunes
export const boardConfigs = {
  'master-branding': {
    status: "Offer ID",
    groups: ["Offer ID", "Completed"],
    pinnedColumnsLeft: ["__check__", "offerID", "threads", "offerTitle"],
    additionalState: {
      offerID: [],
      completed: [],
      payoutAceptado: "",
      cap: "",
      timeframe: "",
      capMensual: "",
      revenueMonedaAnunciante: "",
    },
  },
  
  'tasks-aluna': {
    status: "Open",
    groups: ["Open", "Done"],
    pinnedColumnsLeft: ["__check__", "threads"],
    additionalState: {
      open: [],
      done: [],
    },
  },
  
  'master-performance': {
    status: "Offer ID",
    groups: ["Offer ID", "Inactive", "Not Profitable"],
    pinnedColumnsLeft: ["__check__", "id", "offerID", "threads", "offerTitle"],
    additionalState: {
      offerID: [],
      inactive: [],
      notProfitable: [],
      payoutAceptado: "",
    },
  },
  
  'prospecting': {
    status: "Prospecting",
    groups: ["Prospecting"],
    pinnedColumnsLeft: ["__check__", "threads"],
    additionalState: {
      items: [],
    },
  },
  
  'prospecting-afiliados': {
    status: "Belén Suero",
    groups: [
      "Belén Suero",
      "Camila Velloso", 
      "Carolina Racana",
      "Katalina Palomino",
      "Sara López Graciano",
    ],
    pinnedColumnsLeft: ["__check__", "threads"],
    additionalState: {
      belenSuero: [],
      mariaCamila: [],
      carolinaRacana: [],
      katalinaPalomino: [],
      saraLopez: [],
      capEstimado: "",
    },
  },
  
  'crm': {
    status: "Open",
    groups: ["Open", "Done"],
    pinnedColumnsLeft: ["__check__", "threads"],
    additionalState: {
      open: [],
      done: [],
    },
  },
  
  'solicitud-campana': {
    status: "Nombre de campaña", 
    groups: ["Nombre de campaña"],
    pinnedColumnsLeft: ["__check__", "threads"],
    additionalState: {
      items: [],
      payAffiliate: "",
      trafficSources: [],
      categories: [],
    },
  },
  
  'validaciones': {
    status: new Date().getFullYear(),
    groups: [new Date().getFullYear(), "Previous year"], 
    pinnedColumnsLeft: ["__check__", "threads"],
    additionalState: {
      items: [],
      itemsDone: [],
      totalBill: "",
      monthSpanish: "",
      year: "",
      advertisers: [],
    },
  },
  
  'social-media': {
    status: "Open",
    groups: ["RRSS"],
    pinnedColumnsLeft: ["__check__", "threads"],
    additionalState: {
      items: [],
      itemsDone: [],
    },
  },
  
  'master-design': {
    status: "Open",
    groups: ["Open", "Done"],
    pinnedColumnsLeft: ["__check__", "threads"],
    additionalState: {
      open: [],
      done: [],
    },
  },
  
  'info-trafico-afiliado': {
    status: "Afiliado",
    groups: ["Afiliado"],
    pinnedColumnsLeft: ["__check__", "threads"],
    additionalState: {
      items: [],
      affiliatesManager: [],
      trafficSources: [],
    },
  },
  
  'master-cps': {
    status: "Offer ID",
    groups: ["Offer ID", "Inactive", "Pixel Integration"],
    pinnedColumnsLeft: ["__check__", "id", "offerID", "threads", "offerTitle"],
    additionalState: {
      offerID: [],
      inactive: [],
      pixelIntegration: [],
    },
  },
};

/**
 * Hook para usar configuraciones predefinidas de boards
 * @param {string} boardType - Tipo de board (clave en boardConfigs)
 * @param {Object} overrides - Sobrescribir configuración predefinida
 * @returns {Array} [state, updateState]
 */
export const useBoardStatePreset = (boardType, overrides = {}) => {
  // Obtener configuración base
  const config = boardConfigs[boardType];
  
  // Combinar configuración predefinida con sobrescrituras
  const finalConfig = config ? {
    ...config,
    ...overrides,
    additionalState: {
      ...config.additionalState,
      ...overrides.additionalState,
    },
  } : overrides;
  
  // Mostrar warning si no se encuentra la configuración
  if (!config) {
    console.warn(`Board type "${boardType}" not found in boardConfigs. Using default config.`);
  }
  
  return useBoardState(finalConfig);
};